import { Fragment, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, Copy, Download, Pencil, Plus, RefreshCw, Trash2 } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/Button";
import { CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { HubTableScrollRegion, HubWorkspaceHeader, HubWorkspacePage } from "@/components/layout/HubWorkspace";
import { HubViewportCard } from "@/components/layout/HubViewportCard";
import { SortableTableHeader } from "@/components/ui/SortableTableHeader";
import { Input, Label } from "@/components/ui/Input";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { useHubToast } from "@/components/ui/HubToast";
import { cn, formatTimestamp } from "@/lib/utils";
import { insideSalesQuoteRestricted, isIntegratorAdmin, useIntegratorMe } from "@/lib/me-query";

import {
  deleteQuote,
  downloadQuotePdf,
  duplicateQuote,
  fetchQuotes,
  fetchQuoteSettings,
  INSIDE_SALES_QUOTES_QUERY_KEY,
  type QuoteSummary,
} from "./insideSalesQuotesApi";
import { formatMoneyString } from "./quoteDecimal";
import { restrictedQuoteCopyBlockReason } from "./quoteLineGuardrails";

type SortKey = "number" | "clientName" | "validThrough" | "status" | "createdBy" | "total" | "updatedAt";

export function InsideSalesQuotesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const meQuery = useIntegratorMe();
  const isAdmin = isIntegratorAdmin(meQuery.data?.user);
  const quoteRestricted = insideSalesQuoteRestricted(meQuery.data);
  const { showToast } = useHubToast();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [mine, setMine] = useState(false);
  const [expandedQuoteIds, setExpandedQuoteIds] = useState<Set<string>>(() => new Set());
  const [sortKey, setSortKey] = useState<SortKey>("updatedAt");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const quotesQuery = useQuery({
    queryKey: [...INSIDE_SALES_QUOTES_QUERY_KEY, { mine }],
    queryFn: () => fetchQuotes(mine),
  });
  const quoteSettingsQuery = useQuery({
    queryKey: ["inside-sales", "quotes", "settings"],
    queryFn: fetchQuoteSettings,
  });
  const amountLimit = quoteSettingsQuery.data?.amountLimit ?? 50_000;
  const quotePriceBand = {
    priceLimitMin: quoteSettingsQuery.data?.priceLimitMin ?? 0.95,
    priceLimitMax: quoteSettingsQuery.data?.priceLimitMax ?? 1.05,
  };
  const duplicateMutation = useMutation({
    mutationFn: duplicateQuote,
    onSuccess: async (quote) => {
      await queryClient.invalidateQueries({ queryKey: INSIDE_SALES_QUOTES_QUERY_KEY });
      navigate(`/inside-sales/quotes/${encodeURIComponent(quote.id)}`);
    },
    onError: (error) => showToast({ message: error instanceof Error ? error.message : "Could not duplicate quote.", tone: "error" }),
  });
  const deleteMutation = useMutation({
    mutationFn: deleteQuote,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: INSIDE_SALES_QUOTES_QUERY_KEY });
      showToast({ message: "Quote deleted.", tone: "success" });
    },
    onError: (error) => showToast({ message: error instanceof Error ? error.message : "Could not delete quote.", tone: "error" }),
  });

  const statuses = useMemo(
    () => [...new Set((quotesQuery.data?.quotes ?? []).map((quote) => quote.status))].sort(),
    [quotesQuery.data],
  );
  const rows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const filtered = (quotesQuery.data?.quotes ?? []).filter((quote) => {
      if (status && quote.status !== status) return false;
      if (!needle) return true;
      return [quote.number, quote.clientName, quote.endUserName, quote.createdByDisplayName]
        .some((value) => value?.toLowerCase().includes(needle));
    });
    return filtered.sort((left, right) => {
      const read = (quote: QuoteSummary): string | number => {
        switch (sortKey) {
          case "total":
            return Number(quote.total ?? 0);
          case "createdBy":
            return quote.createdByDisplayName ?? "";
          default:
            return String(quote[sortKey] ?? "");
        }
      };
      const leftValue = read(left);
      const rightValue = read(right);
      const comparison =
        typeof leftValue === "number" && typeof rightValue === "number"
          ? leftValue - rightValue
          : String(leftValue).localeCompare(String(rightValue), undefined, { numeric: true });
      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [quotesQuery.data, search, sortDirection, sortKey, status]);

  function toggleSort(nextKey: SortKey) {
    if (nextKey === sortKey) setSortDirection((current) => (current === "asc" ? "desc" : "asc"));
    else {
      setSortKey(nextKey);
      setSortDirection("asc");
    }
  }

  function toggleQuoteItems(quoteId: string) {
    setExpandedQuoteIds((current) => {
      const next = new Set(current);
      if (next.has(quoteId)) next.delete(quoteId);
      else next.add(quoteId);
      return next;
    });
  }

  return (
    <HubWorkspacePage>
      <HubWorkspaceHeader className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <PageTitleWithHelp hoverHint="Create and manage Hub-native inside-sales quote drafts and PDFs.">
          Quotes
        </PageTitleWithHelp>
        <div className="flex w-full flex-col gap-3 sm:flex-row lg:w-auto lg:items-end">
          <div className="min-w-44">
            <Label htmlFor="quote-status-filter">Status</Label>
            <select
              id="quote-status-filter"
              value={status}
              onChange={(event) => setStatus(event.target.value)}
              className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm"
            >
              <option value="">All statuses</option>
              {statuses.map((option) => <option key={option} value={option}>{option.replaceAll("_", " ")}</option>)}
            </select>
          </div>
          <div className="min-w-72 flex-1">
            <Label htmlFor="quote-search">Search</Label>
            <div className="mt-1 flex gap-2">
              <Input id="quote-search" type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Quote, client, end user, or creator" />
              <Button type="button" variant="ghost" size="sm" aria-label="Refresh quotes" onClick={() => void quotesQuery.refetch()}>
                <RefreshCw className={cn("h-4 w-4", quotesQuery.isFetching && "animate-spin")} aria-hidden />
              </Button>
            </div>
          </div>
          <Button
            type="button"
            variant={mine ? "primary" : "secondary"}
            aria-pressed={mine}
            title="Show quotes whose end-user Freshsales owner matches your hub user."
            onClick={() => setMine((current) => !current)}
          >
            Your accounts
          </Button>
          <Link to="/inside-sales/quotes/new" className="inline-flex">
            <Button type="button">
              <Plus className="h-4 w-4" aria-hidden />
              New quote
            </Button>
          </Link>
        </div>
      </HubWorkspaceHeader>

      <HubViewportCard chrome="extraToolbar" className="min-h-0 flex-1">
        <CardHeader>
          <CardTitle>{rows.length} quotes</CardTitle>
        </CardHeader>
        <CardBody className="flex min-h-0 flex-1 flex-col overflow-hidden p-0">
          {quotesQuery.isLoading ? (
            <p className="px-5 py-4 text-sm text-slate-600">Loading quotes...</p>
          ) : null}
          {quotesQuery.isError ? (
            <p className="px-5 py-4 text-sm text-rose-700">{quotesQuery.error.message}</p>
          ) : null}
          <HubTableScrollRegion>
            <table className="w-full min-w-[980px] border-collapse text-sm">
            <thead className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50/95 backdrop-blur-sm">
              <tr>
                <SortableTableHeader label="Quote" sortKey="number" activeKey={sortKey} dir={sortDirection} onSort={toggleSort} />
                <SortableTableHeader label="Client / end user" sortKey="clientName" activeKey={sortKey} dir={sortDirection} onSort={toggleSort} />
                <SortableTableHeader label="Valid through" sortKey="validThrough" activeKey={sortKey} dir={sortDirection} onSort={toggleSort} />
                <SortableTableHeader label="Status" sortKey="status" activeKey={sortKey} dir={sortDirection} onSort={toggleSort} />
                <th className="px-3 py-2 text-left">Created by</th>
                <SortableTableHeader label="Total" sortKey="total" activeKey={sortKey} dir={sortDirection} onSort={toggleSort} align="right" />
                <SortableTableHeader label="Updated" sortKey="updatedAt" activeKey={sortKey} dir={sortDirection} onSort={toggleSort} />
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((quote: QuoteSummary) => {
                const isExpanded = expandedQuoteIds.has(quote.id);
                const downloadBlocked = quoteRestricted && Number(quote.total) > amountLimit && String(quote.status).toLowerCase() !== "approved";
                const copyBlockReason = quoteRestricted
                  ? restrictedQuoteCopyBlockReason(quote.items, quotePriceBand)
                  : null;
                return (
                <Fragment key={quote.id}>
                <tr className="border-b border-slate-100 hover:[&>td]:bg-slate-50/80">
                  <td className="px-3 py-2"><Link to={`/inside-sales/quotes/${encodeURIComponent(quote.id)}`} className="font-medium text-brand-700 hover:text-brand-800">{quote.number || "Draft"}</Link></td>
                  <td className="px-3 py-2">
                    <div className="font-medium text-slate-900">{quote.endUserName || "No end user"}</div>
                    <div className="text-xs text-slate-500">{quote.clientName || "No client"}</div>
                  </td>
                  <td className="px-3 py-2">{quote.validThrough?.slice(0, 10) || "-"}</td>
                  <td className="px-3 py-2 capitalize">{quote.status.replaceAll("_", " ")}</td>
                  <td className="px-3 py-2">{quote.createdByDisplayName || "-"}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatMoneyString(quote.total)}</td>
                  <td className="px-3 py-2">{formatTimestamp(quote.updatedAt)}</td>
                  <td className="px-3 py-2">
                    <div className="flex justify-end gap-1">
                      <Link to={`/inside-sales/quotes/${encodeURIComponent(quote.id)}`} className="inline-flex">
                        <Button type="button" size="sm" variant="ghost" aria-label={`Edit ${quote.number || "quote"}`}>
                          <Pencil className="h-4 w-4" aria-hidden />
                        </Button>
                      </Link>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        aria-label="Download quote PDF"
                        disabled={downloadBlocked}
                        title={downloadBlocked ? `Quotes over $${amountLimit.toLocaleString()} require approval before download.` : undefined}
                        onClick={() => {
                          void downloadQuotePdf(quote.id, `${quote.number || "quote"}.pdf`).catch((error) => {
                            showToast({
                              message: error instanceof Error ? error.message : "Could not generate the quote PDF.",
                              tone: "error",
                            });
                          });
                        }}
                      ><Download className="h-4 w-4" aria-hidden /></Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        aria-label="Duplicate quote"
                        disabled={copyBlockReason != null}
                        title={copyBlockReason ?? undefined}
                        onClick={() => duplicateMutation.mutate(quote.id)}
                      ><Copy className="h-4 w-4" aria-hidden /></Button>
                      {isAdmin ? (
                        <Button type="button" size="sm" variant="ghost" aria-label="Delete quote" onClick={() => { if (window.confirm("Delete this quote draft?")) deleteMutation.mutate(quote.id); }}><Trash2 className="h-4 w-4" aria-hidden /></Button>
                      ) : null}
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        aria-expanded={isExpanded}
                        aria-label={isExpanded ? `Hide items for ${quote.number || "quote"}` : `Show items for ${quote.number || "quote"}`}
                        onClick={() => toggleQuoteItems(quote.id)}
                      >
                        <ChevronDown className={cn("h-4 w-4 transition-transform", isExpanded && "rotate-180")} aria-hidden />
                      </Button>
                    </div>
                  </td>
                </tr>
                {isExpanded ? (
                  <tr className="border-b border-slate-200 bg-slate-50/60">
                    <td colSpan={8} className="px-6 py-4">
                      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                        <table className="w-full text-sm">
                          <thead className="bg-slate-50 text-left text-xs font-semibold uppercase text-slate-500">
                            <tr>
                              <th className="px-3 py-2 pr-4">SKU</th>
                              <th className="px-3 py-2 pr-4">Description</th>
                              <th className="px-3 py-2 pr-4 text-right">Qty</th>
                              <th className="px-3 py-2 pr-4 text-right">Unit price</th>
                              <th className="px-3 py-2 text-right">Total</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-200">
                            {quote.items.map((item) => (
                              <tr key={item.id}>
                                <td className="px-3 py-2 pr-4 font-medium text-slate-900">{item.sku}</td>
                                <td className="px-3 py-2 pr-4 text-slate-600">{item.description || "-"}</td>
                                <td className="px-3 py-2 pr-4 text-right tabular-nums">{item.quantity}</td>
                                <td className="px-3 py-2 pr-4 text-right tabular-nums">{formatMoneyString(item.unitPrice)}</td>
                                <td className="px-3 py-2 text-right font-medium tabular-nums">{formatMoneyString(item.total || "0")}</td>
                              </tr>
                            ))}
                            {quote.items.length === 0 ? (
                              <tr><td colSpan={5} className="px-3 py-3 text-slate-500">No quote items.</td></tr>
                            ) : null}
                          </tbody>
                        </table>
                      </div>
                    </td>
                  </tr>
                ) : null}
                </Fragment>
              )})}
              {!quotesQuery.isLoading && rows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-3 py-8 text-center text-sm text-slate-500">
                    {quotesQuery.data?.mineOwnerMissing
                      ? "Your hub user is not linked to a Freshsales owner, so Your accounts has nothing to match."
                      : "No quotes match these filters."}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
          </HubTableScrollRegion>
        </CardBody>
      </HubViewportCard>
    </HubWorkspacePage>
  );
}
